import * as React from "react";
import {
  IdentityInput,
  IdentityNativeNetworkResourceAwsInput,
  IdentityNativeNetworkResourceGcpInput,
  IdentityNetworkResourceFqdnInput,
  IdentityNetworkResourceIpInput,
} from "../../schema/types/identity/input";
import { getDefaultBaseInput, OptimisticConcurrency } from "../../schema/types/base";
import { IdentityCreate } from "../../schema/types/identity/create";
import { inputToTags, tagsToInput } from "../../mst/kinds/utils";
import { Identity, NativeNetworkResource, NetworkResource } from "../../schema/types/identity/entity";
import { parseLink } from "../../utils/linkParser/linkParser";
import { v4 as uuidv4 } from "uuid";
import { sentryLog } from "../../tools/sentry";
import { linksOf } from "../../services/cpln";
import { defaultValues } from "../../services/defaults";
import { getDefaultKindSelectState } from "../../components/antd/kindselect/KindSelect";

export function getDefaultIdentityInput(): IdentityInput {
  return {
    ...getDefaultBaseInput(),
    id: "",
    version: 0,
    gvc: getDefaultKindSelectState(),
    aws: {
      isEnabled: false,
      cloudAccount: getDefaultKindSelectState(),
      method: "role",
      policyRefs: {
        error: "",
        filter: "",
        inputMode: "manual",
        selectedPolicies: [],
        manualPolicies: [],
      },
      roleName: {
        error: "",
        filter: "",
        value: "",
      },
    },
    gcp: {
      isEnabled: false,
      method: "serviceAccount",
      cloudAccount: getDefaultKindSelectState(),
      serviceAccount: {
        error: "",
        filter: "",
        value: "",
      },
      bindings: [],
      itemForm: {
        resource: "",
        resourceHref: "",
        roles: [],
      },
    },
    azure: {
      isEnabled: false,
      cloudAccount: getDefaultKindSelectState(),
      roleAssignments: [],
      itemForm: {
        roles: [],
        scope: "",
        scopeHref: "",
      },
    },
    ngs: {
      isEnabled: false,
      cloudAccount: getDefaultKindSelectState(),
      pub: { allow: [], deny: [] },
      sub: { allow: [], deny: [] },
      resp: {
        max: defaultValues.identity.ngs.resp.max.default,
        ttl: "",
      },
      subs: defaultValues.identity.ngs.subs.default,
      data: defaultValues.identity.ngs.data.default,
      payload: defaultValues.identity.ngs.payload.default,
    },
    networkResources: {
      fqdn: [],
      ip: [],
      fqdnItemForm: {
        isAddingItem: false,
        agent: getDefaultKindSelectState(),
        fqdn: "",
        name: "",
        generatedName: "",
        ports: [],
        resolverIp: "",
      },
      ipItemForm: {
        isAddingItem: false,
        agent: getDefaultKindSelectState(),
        name: "",
        ports: [],
        ips: [],
      },
    },
    nativeNetworkResources: {
      aws: [],
      gcp: [],
      awsItemForm: {
        isAddingItem: false,
        cloudAccount: getDefaultKindSelectState(),
        fqdn: "",
        name: "",
        generatedName: "",
        ports: [],
        awsPrivateLink: {
          endpointServiceName: "",
        },
      },
      gcpItemForm: {
        isAddingItem: false,
        cloudAccount: getDefaultKindSelectState(),
        fqdn: "",
        name: "",
        generatedName: "",
        ports: [],
        gcpServiceConnect: {
          targetService: "",
        },
      },
    },
    meta: {
      isEdit: false,
      collapsedCards: [],
    },
  };
}

export function inputToIdentity(input: IdentityInput): IdentityCreate {
  const res: IdentityCreate = {
    name: input.name,
    kind: "identity",
    tags: inputToTags(input.tags),
  };

  if (input.aws.isEnabled) {
    res.aws = {
      cloudAccountLink: input.aws.cloudAccount.link,
    };
    if (input.aws.method === "role") {
      res.aws.roleName = input.aws.roleName.value;
    }
    if (input.aws.method === "policies") {
      if (input.aws.policyRefs.inputMode === "selection") {
        res.aws.policyRefs = input.aws.policyRefs.selectedPolicies;
      } else if (input.aws.policyRefs.inputMode === "manual") {
        res.aws.policyRefs = input.aws.policyRefs.manualPolicies.map((av) => av.value);
      }
    }
  }

  if (input.azure.isEnabled) {
    res.azure = {
      cloudAccountLink: input.azure.cloudAccount.link,
      roleAssignments: input.azure.roleAssignments.map((ra) => ({ roles: ra.roles, scope: ra.scope })),
    };
  }

  if (input.gcp.isEnabled) {
    res.gcp = {
      cloudAccountLink: input.gcp.cloudAccount.link,
    };
    if (input.gcp.method === "serviceAccount") {
      res.gcp.serviceAccount = input.gcp.serviceAccount.value;
    } else if (input.gcp.method === "bindings") {
      res.gcp.bindings = input.gcp.bindings.map((b) => ({ resource: b.resource, roles: b.roles }));
    }
  }

  if (input.ngs.isEnabled) {
    res.ngs = {
      cloudAccountLink: input.ngs.cloudAccount.link,
    };
    if (input.ngs.data !== undefined) {
      res.ngs.data = Number(input.ngs.data);
    }
    if (input.ngs.subs !== undefined) {
      res.ngs.subs = Number(input.ngs.subs);
    }
    if (input.ngs.payload !== undefined) {
      res.ngs.payload = Number(input.ngs.payload);
    }
    if (input.ngs.resp.max !== undefined) {
      if (!res.ngs.resp) {
        res.ngs.resp = {};
      }
      res.ngs.resp.max = Number(input.ngs.resp.max);
    }
    if (input.ngs.resp.ttl) {
      if (!res.ngs.resp) {
        res.ngs.resp = {};
      }
      res.ngs.resp.ttl = input.ngs.resp.ttl;
    }
    if (input.ngs.sub.allow.length > 0) {
      if (!res.ngs.sub) {
        res.ngs.sub = {};
      }
      res.ngs.sub.allow = input.ngs.sub.allow.map((a) => a.value);
    }
    if (input.ngs.sub.deny.length > 0) {
      if (!res.ngs.sub) {
        res.ngs.sub = {};
      }
      res.ngs.sub.deny = input.ngs.sub.deny.map((a) => a.value);
    }
    if (input.ngs.pub.allow.length > 0) {
      if (!res.ngs.pub) {
        res.ngs.pub = {};
      }
      res.ngs.pub.allow = input.ngs.pub.allow.map((a) => a.value);
    }
    if (input.ngs.pub.deny.length > 0) {
      if (!res.ngs.pub) {
        res.ngs.pub = {};
      }
      res.ngs.pub.deny = input.ngs.pub.deny.map((a) => a.value);
    }
  }

  if (input.networkResources.ip.length > 0) {
    if (!res.networkResources) {
      res.networkResources = [];
    }
    res.networkResources.push(
      ...input.networkResources.ip.map((i) => {
        const x: NetworkResource = {
          agentLink: i.agent.link,
          IPs: i.ips,
          name: i.name,
          ports: i.ports.map((p) => Number(p)),
        };

        return x;
      }),
    );
  }
  if (input.networkResources.fqdn.length > 0) {
    if (!res.networkResources) {
      res.networkResources = [];
    }
    res.networkResources.push(
      ...input.networkResources.fqdn.map((i) => {
        const x: NetworkResource = {
          agentLink: i.agent.link,
          FQDN: i.fqdn,
          name: i.name,
          ports: i.ports.map((p) => Number(p)),
        };
        if (i.resolverIp) {
          x.resolverIP = i.resolverIp;
        }
        return x;
      }),
    );
  }

  if (input.nativeNetworkResources.aws.length > 0) {
    if (!res.nativeNetworkResources) {
      res.nativeNetworkResources = [];
    }
    res.nativeNetworkResources.push(
      ...input.nativeNetworkResources.aws.map((i) => {
        const x: NativeNetworkResource = {
          awsPrivateLink: {
            endpointServiceName: i.awsPrivateLink.endpointServiceName,
          },
          FQDN: i.fqdn,
          name: i.name,
          ports: i.ports.map((p) => Number(p)),
        };
        return x;
      }),
    );
  }
  if (input.nativeNetworkResources.gcp.length > 0) {
    if (!res.nativeNetworkResources) {
      res.nativeNetworkResources = [];
    }
    res.nativeNetworkResources.push(
      ...input.nativeNetworkResources.gcp.map((i) => {
        const x: NativeNetworkResource = {
          gcpServiceConnect: {
            targetService: i.gcpServiceConnect.targetService,
          },
          FQDN: i.fqdn,
          name: i.name,
          ports: i.ports.map((p) => Number(p)),
        };
        return x;
      }),
    );
  }

  if (input.description) {
    res.description = input.description;
  }
  return res;
}

export function identityToInput(identity: Identity): IdentityInput {
  const defaultInput = getDefaultIdentityInput();

  const selfLink = linksOf(identity).self!;
  const { org, gvc } = parseLink(selfLink, { useInputCtx: true });

  const res: IdentityInput = {
    ...defaultInput,
    id: identity.id,
    version: identity.version,
    name: identity.name,
    description: identity.description,
    tags: tagsToInput(identity.tags),
    gvc: {
      value: gvc,
      link: `/org/${org}/gvc/${gvc}`,
    },
    meta: {
      ...defaultInput.meta,
      isEdit: true,
    },
  };

  if (identity.aws) {
    const parsedLink = parseLink(identity.aws.cloudAccountLink, { useInputCtx: true });
    res.aws.isEnabled = true;
    res.aws.cloudAccount = {
      link: parsedLink.absolute,
      value: parsedLink.name,
    };
    if (identity.aws.roleName) {
      res.aws.method = "role";
      res.aws.roleName.value = identity.aws.roleName;
    }
    if (identity.aws.policyRefs && Array.isArray(identity.aws.policyRefs) && identity.aws.policyRefs.length > 0) {
      res.aws.method = "policies";
      // Populate both properties so data isn't lost regardless of which inputMode the UI ends up in
      res.aws.policyRefs.selectedPolicies = identity.aws.policyRefs;
      res.aws.policyRefs.manualPolicies = identity.aws.policyRefs.map((p) => ({ id: uuidv4(), value: p }));
    }
  }

  if (identity.azure) {
    const parsedLink = parseLink(identity.azure.cloudAccountLink, { useInputCtx: true });
    res.azure.isEnabled = true;
    res.azure.cloudAccount = {
      link: parsedLink.absolute,
      value: parsedLink.name,
    };
    res.azure.roleAssignments = (identity.azure.roleAssignments || []).map((ra) => ({
      id: uuidv4(),
      scope: ra.scope || "",
      roles: ra.roles || [],
    }));
  }

  if (identity.gcp) {
    const parsedLink = parseLink(identity.gcp.cloudAccountLink, { useInputCtx: true });
    res.gcp.isEnabled = true;
    res.gcp.cloudAccount = {
      link: parsedLink.absolute,
      value: parsedLink.name,
    };
    if (identity.gcp.serviceAccount) {
      res.gcp.method = "serviceAccount";
      res.gcp.serviceAccount.value = identity.gcp.serviceAccount;
    } else if (identity.gcp.bindings && Array.isArray(identity.gcp.bindings) && identity.gcp.bindings.length > 0) {
      res.gcp.method = "bindings";
      res.gcp.bindings = (identity.gcp.bindings || []).map((b) => ({
        id: uuidv4(),
        resource: b.resource || "",
        roles: b.roles || [],
      }));
    }
  }

  if (identity.ngs) {
    const parsedLink = parseLink(identity.ngs.cloudAccountLink, { useInputCtx: true });
    res.ngs.isEnabled = true;
    res.ngs.cloudAccount = {
      link: parsedLink.absolute,
      value: parsedLink.name,
    };
    if (identity.ngs.data !== undefined) {
      res.ngs.data = String(identity.ngs.data);
    }
    if (identity.ngs.subs !== undefined) {
      res.ngs.subs = String(identity.ngs.subs);
    }
    if (identity.ngs.payload !== undefined) {
      res.ngs.payload = String(identity.ngs.payload);
    }
    if (identity.ngs.resp?.max !== undefined) {
      res.ngs.resp.max = String(identity.ngs.resp.max);
    }
    if (identity.ngs.resp?.ttl) {
      res.ngs.resp.ttl = identity.ngs.resp.ttl;
    }
    if (identity.ngs.sub?.allow && Array.isArray(identity.ngs.sub.allow) && identity.ngs.sub.allow.length > 0) {
      res.ngs.sub.allow = identity.ngs.sub.allow.map((a) => ({ id: uuidv4(), value: a }));
    }
    if (identity.ngs.sub?.deny && Array.isArray(identity.ngs.sub.deny) && identity.ngs.sub.deny.length > 0) {
      res.ngs.sub.deny = identity.ngs.sub.deny.map((a) => ({ id: uuidv4(), value: a }));
    }
    if (identity.ngs.pub?.allow && Array.isArray(identity.ngs.pub.allow) && identity.ngs.pub.allow.length > 0) {
      res.ngs.pub.allow = identity.ngs.pub.allow.map((a) => ({ id: uuidv4(), value: a }));
    }
    if (identity.ngs.pub?.deny && Array.isArray(identity.ngs.pub.deny) && identity.ngs.pub.deny.length > 0) {
      res.ngs.pub.deny = identity.ngs.pub.deny.map((a) => ({ id: uuidv4(), value: a }));
    }
  }

  if (identity.networkResources && Array.isArray(identity.networkResources) && identity.networkResources.length > 0) {
    for (const networkResource of identity.networkResources) {
      if (!networkResource.agentLink) {
        sentryLog("identity network resource dont have agent", {
          "data.link": linksOf(identity).self!,
        });
        continue;
      }
      const parsedLink = parseLink(networkResource.agentLink!, { useInputCtx: true });
      const agentState = {
        value: parsedLink.name,
        name: parsedLink.name,
        link: parsedLink.absolute,
      };
      if (networkResource.FQDN) {
        const fqdnInput: IdentityNetworkResourceFqdnInput = {
          id: uuidv4(),
          agent: agentState,
          fqdn: networkResource.FQDN,
          name: networkResource.name,
          ports: networkResource.ports.map((p) => String(p)),
          resolverIp: networkResource.resolverIP || "",
        };
        res.networkResources.fqdn.push(fqdnInput);
      } else if (networkResource.IPs && Array.isArray(networkResource.IPs) && networkResource.IPs.length > 0) {
        const ipInput: IdentityNetworkResourceIpInput = {
          id: uuidv4(),
          agent: agentState,
          ips: networkResource.IPs.map((p) => String(p)),
          name: networkResource.name,
          ports: networkResource.ports.map((p) => String(p)),
        };
        res.networkResources.ip.push(ipInput);
      }
    }
  }

  if (
    identity.nativeNetworkResources &&
    Array.isArray(identity.nativeNetworkResources) &&
    identity.nativeNetworkResources.length > 0
  ) {
    for (const networkResource of identity.nativeNetworkResources) {
      if (networkResource.awsPrivateLink?.endpointServiceName) {
        const awsInput: IdentityNativeNetworkResourceAwsInput = {
          id: uuidv4(),
          awsPrivateLink: {
            endpointServiceName: networkResource.awsPrivateLink.endpointServiceName,
          },
          fqdn: networkResource.FQDN || "",
          name: networkResource.name || "",
          ports: networkResource.ports.map((p) => String(p)),
        };
        res.nativeNetworkResources.aws.push(awsInput);
      } else if (networkResource.gcpServiceConnect?.targetService) {
        const gcpInput: IdentityNativeNetworkResourceGcpInput = {
          id: uuidv4(),
          gcpServiceConnect: {
            targetService: networkResource.gcpServiceConnect.targetService,
          },
          fqdn: networkResource.FQDN || "",
          name: networkResource.name || "",
          ports: networkResource.ports.map((p) => String(p)),
        };
        res.nativeNetworkResources.gcp.push(gcpInput);
      }
    }
  }

  return res;
}

export function identityToPatch(_identity: IdentityCreate, current: Identity, concurrency: OptimisticConcurrency) {
  const identity: any = JSON.parse(JSON.stringify(_identity));

  if (current.aws && !_identity.aws) {
    identity.aws = null as any;
  } else if (current.aws && _identity.aws) {
    identity[`$replace/aws`] = identity.aws;
    delete identity.aws;
  }

  if (current.azure && !_identity.azure) {
    identity.azure = null as any;
  } else if (current.azure && _identity.azure) {
    identity[`$replace/azure`] = identity.azure;
    delete identity.azure;
  }

  if (current.gcp && !_identity.gcp) {
    identity.gcp = null as any;
  } else if (current.gcp && _identity.gcp) {
    identity[`$replace/gcp`] = identity.gcp;
    delete identity.gcp;
  }

  if (current.ngs && !_identity.ngs) {
    identity.ngs = null as any;
  } else if (current.ngs && _identity.ngs) {
    identity[`$replace/ngs`] = identity.ngs;
    delete identity.ngs;
  }

  identity[`$replace/networkResources`] = identity.networkResources;
  delete identity.networkResources;

  identity[`$replace/nativeNetworkResources`] = identity.nativeNetworkResources;
  delete identity.nativeNetworkResources;

  identity[`$replace/tags`] = identity.tags;
  delete identity.tags;

  identity.id = concurrency.id;
  identity.version = concurrency.version;

  return identity;
}
